import { useEffect, useRef, useState, type RefObject } from "react";

/** About the sticky bar's height (`.public-top`): 40px of logo and 12px above and below. */
const BAR_PX = 64;

/**
 * Whether the element has scrolled up out of view -- the profile's name
 * under the sticky bar --, so the bar takes the business over from it. False
 * until the browser says otherwise: the bar starts quiet, as the sign is
 * what the page opens on. `drawn`: whether the element is on the page yet --
 * an item's strip arrives with its profile, after the page --, so watching
 * starts when it does.
 */
export function useScrolledPast<T extends Element>(drawn = true): { ref: RefObject<T | null>; past: boolean } {
  const ref = useRef<T | null>(null);
  const [past, setPast] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (node === null) return;
    // The bar's own height off the top: a name gone under the bar is gone.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry !== undefined) setPast(!entry.isIntersecting && entry.boundingClientRect.top < BAR_PX);
      },
      { rootMargin: `-${String(BAR_PX)}px 0px 0px 0px` },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [drawn]);
  return { ref, past };
}
